import { useState } from 'react';
import { Modal, Radio, Select, Space, Button, message, InputNumber, Alert, Upload, Typography, Card } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useAuth } from '../store/auth';
import { BPMService } from '../api/services/bpm.service';

const { Text } = Typography;

export default function BpmStartModal({ open, onCancel, onSuccess, task, project, users, allFiles }) {
  const { user } = useAuth();
  const [processType, setProcessType] = useState('custom');
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [durationDays, setDurationDays] = useState(3);
  const [selectedDirectorId, setSelectedDirectorId] = useState(null);
  const [selectedEditorId, setSelectedEditorId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  const isUnderConstruction = ['contract', 'claim', 'invoice'].includes(processType);

  const handleStartProcess = async () => {
    if (isUnderConstruction) {
      message.info('Маршрут в разработке');
      return;
    }
    
    if (processType !== 'others' && !selectedFileId) {
      message.warning('Выберите документ');
      return;
    }
    if (processType === 'others' && fileList.length === 0) {
      message.warning('Прикрепите файлы');
      return;
    }
    if (processType === 'others' && !selectedEditorId) {
      message.warning('Выберите сотрудника с правом редактирования');
      return;
    }
    if (processType === 'custom' && selectedParticipants.length === 0) {
      message.warning('Выберите хотя бы одного согласующего');
      return;
    }
    
    setLoading(true);
    
    try {
      if (processType === 'others') {
         // Create FormData for multiupload and new endpoint
         // Currently BPMService doesn't have an endpoint for this, we simulate it
         const formData = new FormData();
         fileList.forEach(f => {
            formData.append('files', f.originFileObj);
         });
         formData.append('duration_days', durationDays);
         formData.append('editor_id', selectedEditorId);
         if (task) formData.append('task_id', task.id);
         if (project) formData.append('project_id', project.id);
         
         await BPMService.startOthersProcess(formData);
         message.success('Отправлено на согласование (ПС)');
      } else {
         let selectedFile = null;
         const attachments = task?.attachments || project?.attachments || [];
         const comments = task?.comments || project?.comments || [];
         
         selectedFile = attachments.find(a => a.file_id === selectedFileId);
         if (!selectedFile) {
             const commentWithFile = comments.find(c => c.file_url && c.file_url.includes(selectedFileId));
             if (commentWithFile) {
                 selectedFile = { file_id: selectedFileId, original_name: commentWithFile.file_name || 'Документ' };
             }
         }
         if (!selectedFile) {
             selectedFile = { file_id: selectedFileId, original_name: 'Выбранный документ' };
         }

         await BPMService.startProcess({
           task_id: task?.id,
           project_id: project?.id,
           target_file_id: selectedFileId,
           target_file_name: selectedFile.original_name,
           title: `Согласование документа ${selectedFile.original_name}`,
           participant_ids: processType === 'kp' ? [] : selectedParticipants,
           duration_days: durationDays,
           process_type: processType,
           director_id: selectedDirectorId,
           editor_id: selectedEditorId
         });
         message.success('Процесс согласования запущен');
      }
      
      onSuccess();
      handleCancel();
    } catch (err) {
      let errorMsg = 'Ошибка при запуске процесса';
      if (err.response?.data?.detail) {
        errorMsg = typeof err.response.data.detail === 'string' ? err.response.data.detail : JSON.stringify(err.response.data.detail);
      }
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFileId(null);
    setSelectedParticipants([]);
    setSelectedDirectorId(null);
    setSelectedEditorId(null);
    setDurationDays(3);
    setProcessType('custom');
    setFileList([]);
    onCancel();
  };

  return (
    <Modal
      title="Запуск процесса согласования"
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>Отмена</Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleStartProcess} disabled={isUnderConstruction}>
          Запустить
        </Button>
      ]}
      width={700}
    >
      <Space direction="vertical" style={{ width: '100%', marginTop: 16 }} size="large">
        <Radio.Group 
            value={processType} 
            onChange={(e) => setProcessType(e.target.value)}
            style={{ width: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
              <Radio value="custom">Произвольный маршрут</Radio>
              <Radio value="kp">Согласование КП</Radio>
              <Radio value="contract">Согласование договора</Radio>
              <Radio value="claim">Согласование претензии</Radio>
              <Radio value="invoice">Согласование счета</Radio>
              <Radio value="others">Прочие согласования</Radio>
          </Space>
        </Radio.Group>

        {isUnderConstruction && (
          <Alert message="🚧 Маршрут находится в разработке" type="warning" showIcon />
        )}

        {!isUnderConstruction && (
          <Card size="small" title="Параметры маршрута">
            <Space direction="vertical" style={{ width: '100%' }}>
              
              {processType === 'kp' && (
                user?.supervisor_id ? (
                  <Alert message={`Ваш руководитель (ID: ${user.supervisor_id}) будет добавлен автоматически.`} type="info" />
                ) : (
                  <Alert message="Нет прямого руководителя в структуре." type="warning" />
                )
              )}

              {processType !== 'others' && (
                  <div>
                      <Text strong>Документ:</Text>
                      <Select
                        style={{ width: '100%', marginTop: 4 }}
                        placeholder="Выберите документ"
                        value={selectedFileId}
                        onChange={setSelectedFileId}
                        options={allFiles.map(f => ({ value: f.id, label: f.name }))}
                      />
                  </div>
              )}
              
              {processType === 'others' && (
                 <div>
                    <Text strong>Файлы для согласования:</Text>
                    <Upload
                        multiple
                        beforeUpload={() => false}
                        fileList={fileList}
                        onChange={({ fileList }) => setFileList(fileList)}
                    >
                        <Button icon={<UploadOutlined />} style={{ marginTop: 8 }}>Загрузить файлы</Button>
                    </Upload>
                 </div>
              )}

              <div>
                  <Text strong>Сотрудник с правом редактирования:</Text>
                  <Select
                    style={{ width: '100%', marginTop: 4 }}
                    placeholder="Выберите сотрудника"
                    value={selectedEditorId}
                    onChange={setSelectedEditorId}
                    options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
                    showSearch
                    optionFilterProp="label"
                  />
              </div>

              {processType === 'custom' && (
                <div>
                  <Text strong>Согласующие:</Text>
                  <Select
                    mode="multiple"
                    style={{ width: '100%', marginTop: 4 }}
                    placeholder="Выберите согласующих"
                    value={selectedParticipants}
                    onChange={setSelectedParticipants}
                    options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
                    showSearch
                    optionFilterProp="label"
                  />
                </div>
              )}

              <div>
                  <Text strong>Срок согласования (дней):</Text>
                  <InputNumber
                    style={{ width: '100%', marginTop: 4 }}
                    min={1}
                    value={durationDays}
                    onChange={setDurationDays}
                  />
              </div>

            </Space>
          </Card>
        )}
      </Space>
    </Modal>
  );
}